import { useProxyStore } from '../store/proxy-store';
import { readProxyStatusSafely } from './readProxyStatusSafely';
import { formatUpstreamProxyForIpc } from '../electron-shared/utils/proxy.util';

type EnsureProxyRunningResult =
  | { success: true; port: number | null }
  | { success: false; error: string };

export async function ensureProxyRunningForPortalTools(): Promise<EnsureProxyRunningResult> {
  const { settings, isProxyRunning, setProxyRunning } =
    useProxyStore.getState();

  const realStatus = await readProxyStatusSafely();

  if (realStatus?.isRunning) {
    if (!isProxyRunning) {
      setProxyRunning(true);
    }

    return { success: true, port: realStatus.port ?? settings.port };
  }

  const result = await window.electronAPI.proxy.start({
    host: settings.host,
    port: settings.port,
    interceptEnabled: settings.interceptEnabled,
    enableHttpsInterception: settings.enableHttps,
    httpsPort: settings.httpsPort,
    autoGenerateCerts: settings.autoGenerateCerts,
    certificatePath: settings.certificatePath,
    keyPath: settings.keyPath,
    caCertPath: settings.caCertPath,
    upstreamProxy: formatUpstreamProxyForIpc({
      upstreamEnabled: settings.upstreamEnabled,
      upstreamHost: settings.upstreamHost,
      upstreamPort: settings.upstreamPort,
    }),
    upstreamAuth:
      settings.upstreamEnabled && settings.upstreamAuth?.username
        ? {
            username: settings.upstreamAuth.username,
            password: settings.upstreamAuth.password ?? '',
          }
        : undefined,
    validateUpstreamTls: settings.validateUpstreamTls,
  });

  if (!result.success) {
    const recheckStatus = await readProxyStatusSafely();

    if (recheckStatus?.isRunning) {
      setProxyRunning(true);

      return {
        success: true,
        port: recheckStatus.port ?? settings.port,
      };
    }

    setProxyRunning(false);

    return {
      success: false,
      error: result.error || 'Failed to start proxy',
    };
  }

  setProxyRunning(true);

  return { success: true, port: result.port ?? settings.port };
}
